/**
 * UniversalMatchIntro — fullscreen overlay above the Pixi canvas that
 * runs the SAME 3-phase start sequence for every game in Tile Tavern.
 *
 *     [INTRO 3000ms] -> [TUTORIAL 10000ms] -> [COUNTDOWN N*1000ms] -> GO
 *
 * Round 1: full pipeline (intro + tutorial + countdown).
 * Rounds 2+: skip tutorial (players already know).
 *
 * Server lands `startsAt` at GO. Total warmup window = INTRO_MS +
 * (round===1 ? TUTORIAL_MS : 0) + countdownSec*1000. The countdown
 * portion is deterministically derived from `startsAt - countdownSec*1000`
 * so the per-second tick + GO event line up exactly with the server's
 * play-start.
 *
 * This component REPLACES:
 *   - the old `MatchIntroOverlay` (React 2000ms cinematic).
 *   - per-game `showRoundIntroCard` "tutorial card" (Pixi).
 *   - per-game 3-2-1-GO Pixi/DOM countdowns.
 *   - bespoke `relay-forge` 4-stage explainer overlay.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { GameTile, InputBindings } from '../../lib/api';
import type { LobbyPlayerView } from '@tt/lobby';
import { audio } from '@tt/audio';
import { IconAvatar } from '../characters/IconAvatar';

export const UMI_INTRO_MS = 3000;
export const UMI_TUTORIAL_MS = 10000;

interface Props {
  matchId: string;
  game: GameTile | undefined;
  fallbackGameId: string;
  /** Server's `matchStartsAt` — the moment GO fires. */
  startsAt: number;
  /** Pure 3-2-1 countdown length in seconds. From lobby setting. */
  countdownSec: number;
  /** Round 1 plays all 3 phases; rounds 2+ skip the tutorial. */
  isFirstRound: boolean;
  mySlot: 0 | 1 | 2 | 3;
  players: LobbyPlayerView[];
  onComplete?: () => void;
}

type Phase = 'intro' | 'tutorial' | 'countdown' | 'go' | 'done';

export function UniversalMatchIntro({
  matchId, game, fallbackGameId, startsAt, countdownSec,
  isFirstRound, mySlot, players, onComplete,
}: Props) {
  // Reduced-motion: collapse intro/tutorial to 1ms each. Countdown still
  // fires the per-second ticks on schedule (audio cues stay).
  const reduce = useMemo(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }, []);
  const introMs = reduce ? 1 : UMI_INTRO_MS;
  const tutorialMs = reduce ? 1 : (isFirstRound ? UMI_TUTORIAL_MS : 0);

  // Total warmup the server reserved for this match. Anchor everything to
  // `startsAt` so per-second flips line up with the server's GO.
  const countdownMs = countdownSec * 1000;
  // intro starts at startsAt - introMs - tutorialMs - countdownMs
  // tutorial starts at startsAt - tutorialMs - countdownMs (skipped if !isFirstRound)
  // countdown starts at startsAt - countdownMs
  const introStartAt = startsAt - introMs - tutorialMs - countdownMs;
  const tutorialStartAt = startsAt - tutorialMs - countdownMs;
  const countdownStartAt = startsAt - countdownMs;

  const [phase, setPhase] = useState<Phase>(() => {
    const now = Date.now();
    if (now < tutorialStartAt) return 'intro';
    if (now < countdownStartAt) return 'tutorial';
    if (now < startsAt) return 'countdown';
    if (now < startsAt + 700) return 'go';
    return 'done';
  });
  const [tickLabel, setTickLabel] = useState<string>('');
  const completeFiredRef = useRef(false);
  const lastTickSecRef = useRef<number>(-1);
  const goFiredRef = useRef(false);
  // Capture the latest onComplete so the rAF effect (keyed only by matchId)
  // always calls the current handler without re-running the loop.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    // Reset per-match guards. Effect re-runs only when matchId changes
    // (parent also re-keys the component, so this is a fresh mount).
    let cancelled = false;
    let raf = 0;
    completeFiredRef.current = false;
    goFiredRef.current = false;
    lastTickSecRef.current = -1;

    const drive = () => {
      if (cancelled) return;
      const now = Date.now();
      // Phase progression
      if (now < tutorialStartAt) {
        setPhase('intro');
      } else if (now < countdownStartAt) {
        // In tutorial window — show tutorial on round 1, otherwise jump
        // straight to countdown (we'll re-evaluate next frame).
        setPhase(isFirstRound ? 'tutorial' : 'countdown');
      } else if (now < startsAt) {
        setPhase('countdown');
        const remainMs = startsAt - now;
        const sec = Math.ceil(remainMs / 1000);
        if (sec !== lastTickSecRef.current) {
          // First-paint silent; otherwise tick exactly once per integer second.
          if (lastTickSecRef.current !== -1 && sec > 0) {
            try { audio.sfx.countdownTick?.(); } catch { /* */ }
          }
          lastTickSecRef.current = sec;
          setTickLabel(String(Math.max(1, sec)));
        }
      } else if (now < startsAt + 700) {
        // GO window. Fire chime exactly once via ref guard — closure-captured
        // `phase` would be stale here and cause the chime to repeat every frame.
        if (!goFiredRef.current) {
          goFiredRef.current = true;
          setPhase('go');
          setTickLabel('GO!');
          try { audio.sfx.countdownGo?.(); } catch { /* */ }
        }
      } else {
        if (!completeFiredRef.current) {
          completeFiredRef.current = true;
          try { onCompleteRef.current?.(); } catch { /* */ }
        }
        setPhase('done');
        return; // stop driving
      }
      raf = requestAnimationFrame(drive);
    };
    raf = requestAnimationFrame(drive);
    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  if (phase === 'done') return null;

  const gameName = game?.name ?? fallbackGameId;
  const pitch = game?.pitch ?? '';
  const tutorialHeadline = game?.tutorial?.headline ?? game?.pitch ?? '';
  const tutorialTip = game?.tutorial?.tip ?? '';
  const category = game?.category ?? 'reflex';
  const sortedPlayers = [...players].sort((a, b) => a.slot - b.slot);

  // Tutorial progress bar: how much of the 10s tutorial has elapsed.
  const tutorialPct = (() => {
    if (phase !== 'tutorial') return phase === 'intro' ? 0 : 100;
    const elapsed = Date.now() - tutorialStartAt;
    return Math.max(0, Math.min(100, (elapsed / Math.max(1, tutorialMs)) * 100));
  })();

  const { pcLines, mobileLines } = derivedControls(game?.inputBindings, game?.controls);

  return (
    <div className={`umi-overlay umi-phase-${phase}`} aria-hidden="true" role="presentation">
      <div className="umi-vignette" />

      {/* Phase 1: INTRO */}
      {phase === 'intro' && (
        <div className="umi-intro" data-cat={category}>
          <div className="umi-intro-kicker">Now playing</div>
          <h1 className="umi-intro-title" data-cat={category}>{gameName}</h1>
          {pitch && <p className="umi-intro-purpose">{pitch}</p>}
          <div className="umi-intro-meta">
            <span className="umi-meta-pill">{category}</span>
            {game?.durationSec ? <span className="umi-meta-pill">{game.durationSec}s</span> : null}
            {game?.chaoticness ? <span className="umi-meta-pill">{'⚡'.repeat(Math.min(5, game.chaoticness))}</span> : null}
          </div>
          <div className="umi-intro-players">
            {sortedPlayers.map((p, i) => (
              <div
                key={p.charId}
                className={`umi-intro-card${p.slot === mySlot ? ' is-me' : ''}`}
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <IconAvatar icon={p.icon} size={48} alt={p.name} />
                <strong>{p.name}</strong>
                <small>P{p.slot + 1}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 2: TUTORIAL — round 1 only */}
      {phase === 'tutorial' && (
        <div className="umi-tutorial" data-cat={category}>
          <div className="umi-tutorial-heading">How to play</div>
          <h2 className="umi-tutorial-title">{gameName}</h2>
          <div className="umi-tutorial-cols">
            <div className="umi-tutorial-col umi-tutorial-col-pc">
              <div className="umi-tutorial-col-label">PC</div>
              <ul className="umi-tutorial-list">
                {pcLines.length === 0 && <li className="muted">— no keyboard —</li>}
                {pcLines.map((line, i) => (
                  <li key={i}>{renderControlLine(line)}</li>
                ))}
              </ul>
            </div>
            <div className="umi-tutorial-col umi-tutorial-col-mobile">
              <div className="umi-tutorial-col-label">Mobile</div>
              <ul className="umi-tutorial-list">
                {mobileLines.length === 0 && <li className="muted">— no touch hint —</li>}
                {mobileLines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="umi-tutorial-objective">
            <div className="umi-tutorial-objective-label">Objective</div>
            <div className="umi-tutorial-objective-headline">{tutorialHeadline}</div>
            {tutorialTip && <div className="umi-tutorial-objective-tip">{tutorialTip}</div>}
          </div>
          <div className="umi-tutorial-progress" aria-hidden="true">
            <div className="umi-tutorial-progress-fill" style={{ width: `${tutorialPct}%` }} />
            <span className="umi-tutorial-progress-label">Get ready…</span>
          </div>
        </div>
      )}

      {/* Phase 3: COUNTDOWN (3-2-1) */}
      {phase === 'countdown' && (
        <div className="umi-countdown">
          <div className="umi-countdown-numeral" data-num={tickLabel}>{tickLabel}</div>
          <div className="umi-countdown-sub">{gameName}</div>
        </div>
      )}

      {/* Phase 4: GO! (brief) */}
      {phase === 'go' && (
        <div className="umi-go">
          <div className="umi-go-numeral">GO!</div>
        </div>
      )}
    </div>
  );
}

// ── Control derivation ───────────────────────────────────────────────

function derivedControls(
  bindings: InputBindings | undefined,
  controls: string[] | undefined,
): { pcLines: ControlLine[]; mobileLines: string[] } {
  const pc: ControlLine[] = [];
  const mobile: string[] = [];

  if (bindings) {
    if (bindings.primary) {
      const { label, key } = bindings.primary;
      if (key && key.length > 0) pc.push({ label, keys: key });
      mobile.push(label);
    }
    if (bindings.alt) {
      for (const a of bindings.alt) {
        if (a.key && a.key.length > 0) pc.push({ label: a.label, keys: a.key });
        else mobile.push(a.label);
      }
    }
  }

  // Fallback: if neither column got anything from inputBindings, splay
  // the manifest.controls array into the mobile column (these are mostly
  // gesture verbs like "tap" / "draw").
  if (pc.length === 0 && mobile.length === 0 && controls && controls.length > 0) {
    for (const c of controls) mobile.push(c);
  }
  return { pcLines: pc, mobileLines: mobile };
}

interface ControlLine {
  label: string;
  keys: string[];
}

function renderControlLine(line: ControlLine) {
  const pretty = line.keys.map(prettyKey);
  return (
    <span className="umi-control-line">
      <span className="umi-control-label">{line.label}</span>
      <span className="umi-control-keys">
        {pretty.map((k, i) => (
          <kbd key={i} className="umi-kbd">{k}</kbd>
        ))}
      </span>
    </span>
  );
}

function prettyKey(code: string): string {
  switch (code) {
    case 'Space': return 'Space';
    case 'Enter': return 'Enter';
    case 'ArrowUp': return '↑';
    case 'ArrowDown': return '↓';
    case 'ArrowLeft': return '←';
    case 'ArrowRight': return '→';
    case 'Digit1': return '1';
    case 'Digit2': return '2';
    case 'Digit3': return '3';
    case 'Digit4': return '4';
    case 'ShiftLeft':
    case 'ShiftRight': return 'Shift';
    case 'ControlLeft':
    case 'ControlRight': return 'Ctrl';
    default:
      if (code.startsWith('Key')) return code.slice(3);
      return code;
  }
}
